import { useEffect, useRef, useState } from 'react';
import { useToastContext } from '@/contexts/toastContext';
import useApiSubmit from '@/hooks/useApiSubmit';
import userServices from '@/services/user';
import Modal from '@/trio-ui/Modal';
import { CreatedUser, EditUserOptions, InviteUser, InviteUserResponse, User } from '@/types/api';
import NewUserFormModalComponent, {
  FormData,
  NewUserFormModalComponentProps,
} from './NewUserFormModal.component';

type NewUserFormModalProps = Pick<
  NewUserFormModalComponentProps,
  'isOpen' | 'onClose' | 'editing'
> & {
  onInviteNewUser?: (user: CreatedUser) => void;
  onEditUser?: (user: User) => void;
  onRemoveUser?: (userId: string) => void;
};

const NewUserFormModal = ({
  isOpen,
  onClose,
  onInviteNewUser,
  onEditUser,
  editing,
  onRemoveUser,
}: NewUserFormModalProps): JSX.Element => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [confirmDelete, setConfirmDelete] = useState<string>();
  const toast = useToastContext();

  useEffect(() => {
    const onPageClick = (event: any): void => {
      if (!isOpen) return;

      const eventPath = event.composedPath() ?? [];

      const doesElementContainId = (element: any): boolean => {
        return element === elementRef.current;
      };

      const isClickingInsideForm = eventPath.some(doesElementContainId);

      if (!confirmDelete && !isClickingInsideForm) {
        onClose?.();
      }
    };

    window.addEventListener('mousedown', onPageClick);

    return () => {
      window.removeEventListener('mousedown', onPageClick);
    };
  }, [elementRef, onClose, isOpen, confirmDelete]);
  // }, [elementRef, onClose, isOpen, lockDrawer, confirmDelete]);

  const inviteUserMutation = useApiSubmit<InviteUserResponse, InviteUser>(userServices.inviteUser);
  const editUserMutation = useApiSubmit<User, EditUserOptions>(userServices.editUser);

  const deleteUserMutation = useApiSubmit<string, string>(userServices.deleteUser);

  const handleSubmit = async (data: FormData): Promise<void> => {
    try {
      if (editing) {
        const editedUser = await editUserMutation.mutateAsync({
          id: editing.id,
          email: data.email,
          roles: data.roles,
        });
        toast.setMessage('User edited successfully.');
        onEditUser?.(editedUser);
      } else if (data.usePassword && data.password) {
        const { user: newUser } = await inviteUserMutation.mutateAsync({
          name: data.email.split('@')[0],
          email: data.email,
          roles: data.roles,
          usePassword: true,
          password: data.password,
        });
        toast.setMessage('User created. They will be asked to reset their password on first login.');
        onInviteNewUser?.(newUser);
      } else {
        const { user: newUser } = await inviteUserMutation.mutateAsync({
          name: data.email.split('@')[0],
          email: data.email,
          roles: data.roles,
        });
        toast.setMessage('User invited successfully.');
        onInviteNewUser?.(newUser);
      }
      toast.setSeverity('success');
      toast.showToast();
      onClose?.();
    } catch (error: any) {
      toast.setMessage(
        error?.response?.data?.message ||
          `There was an error ${editing ? 'editing' : 'inviting'} the user.`
      );
      toast.setSeverity('error');
      toast.showToast();
    }
  };

  const handleDeleteConfirmClick = async (userId: string): Promise<void> => {
    try {
      await deleteUserMutation.mutateAsync(userId);
      toast.setMessage('User removed successfully.');
      toast.setSeverity('success');
      toast.showToast();
      onRemoveUser?.(userId);
      onClose?.();
    } catch (error: any) {
      toast.setMessage(error?.response?.data?.message || 'There was an error removing the user.');
      toast.setSeverity('error');
      toast.showToast();
    } finally {
      setConfirmDelete(undefined);
    }
  };

  const handleDeleteClick = (userId: string): void => {
    setConfirmDelete?.(userId);
  };

  return (
    <>
      <NewUserFormModalComponent
        isOpen={isOpen}
        onClose={onClose}
        ref={elementRef}
        onSubmit={handleSubmit}
        isLoading={inviteUserMutation.isLoading || editUserMutation.isLoading}
        editing={editing}
        onDeleteClick={handleDeleteClick}
      />
      <Modal
        isCentered
        size="sm"
        modalTitle="Are you sure?"
        bodyRender={<>Are you sure you want to remove it? This action cannot be undone.</>}
        onConfirmLabel="Yes, remove it"
        onConfirmButtonVariant="red"
        onConfirmClick={() => {
          handleDeleteConfirmClick(confirmDelete || '');
        }}
        isButtonDisabled={deleteUserMutation.isLoading}
        onClose={() => setConfirmDelete(undefined)}
        isOpen={Boolean(confirmDelete)}
      />
    </>
  );
};

export default NewUserFormModal;
